import { AddCircle } from 'assets/icons/AddCircle'
import { CloseIcon } from 'assets/icons/CloseIcon'
import RemoveCircle from 'assets/icons/RemoveCircle'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { addInvField, editInvField } from 'features/inventory/invSettingsFieldsSlice'
import useModalClose from 'hooks/useModalClose'
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Button, CursorPointer, Flex, ModalScrollContainer, ThinSeparator } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import useFocusFirstInput from 'utils/useFocusFirstInput'

export const InventoryFieldsModal = ({ closeModal, value }: { closeModal: any, value?: IInventoryFieldsModal }) => {
    const [state, setState] = useState(value || {
        id: null,
        label: null,
        dataType: null,
        unit: null,
        helpText: null,
        dropDownItems: []
    })
    const dispatch = useDispatch<AppDispatch>()
    useFocusFirstInput()
    useModalClose(closeModal)

    const handleSave = async () => {
        const resp = await dispatch(value?.id ? editInvField(state) : addInvField(state))
        if (resp.meta.requestStatus === 'fulfilled') {
            closeModal()
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, id?: number) => {
        if (id) {
            const newState = state.dropDownItems.map((item) => {
                if (item.id === id) {
                    return { ...item, [e.target.name]: e.target.value }
                }
                return item
            })
            setState({ ...state, dropDownItems: newState })
            return
        }
        setState({ ...state, [e.target.name]: e.target.value })
    }

    const handleSelect = (v: ISelectValue, n?: string, id?: number) => {

        if (id) {
            const newState = state.dropDownItems.map((item) => {
                if (item.id === id) {
                    return { ...item, [n]: v }
                }
                return item
            })
            setState({ ...state, dropDownItems: newState })
            return
        }

        setState({
            ...state, [n]: v, dropDownItems: n == 'dataType' && v.value == 3 ? [
                { id: new Date().getMilliseconds(), name: '', type: null },
                { id: new Date().getMilliseconds() + 1, name: '', type: null },
            ] : []
        })

    }


    const handleAddSubfield = () => {
        setState((prev) => {
            return {
                ...prev,
                dropDownItems: [
                    ...prev.dropDownItems,
                    { id: new Date().getMilliseconds(), name: '', type: null },
                ],
            }
        })
    }

    const handleRemoveField = (id: number) => {
        setState((prev) => {
            return { ...prev, dropDownItems: prev.dropDownItems.filter((item) => item.id !== id) }
        })
    }

    const handleUnit = (v: ISelectValue) => {
        setState((prev) => ({ ...prev, unit: v }))
    }

    const isDisabled = useMemo(() => {
        if (!state.label || !state.dataType) return true
        if (state.dataType.value == 2 && !state.unit?.value) return true
        if (state.dataType.value == 3) {
            if (state.dropDownItems.length < 2) return true
            for (const item of state.dropDownItems) {
                if (!item.name || !item.type) return true
            }
        }
        return false
    }, [state])


    return <Modal black>
        <Flex width='100%' between center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold' >{value?.id ? 'Edit' : 'Add'} Field</TitleText>
            <CursorPointer onClick={closeModal} >
                <CloseIcon />
            </CursorPointer>
        </Flex>
        <ModalScrollContainer style={{ marginBottom: '1rem' }} >
            <Flex width='100%' column gap='1rem'>
                <Input placeholder='Enter name' labelText='Name' name='label' value={state.label} onChange={handleChange} nomarg />

                <HonestSelectInput
                    nomarg
                    onChange={handleSelect}
                    options={fieldTypes}
                    labelText='Type'
                    name='dataType'
                    defaultValue={state?.dataType?.value}
                />
                {state.dataType?.value == 3 && (
                    <>
                        <OtherText weight='bold'>Subfields:</OtherText>
                        <ThinSeparator margin='2px' />
                    </>
                )}
                {state.dropDownItems.map((s) => (
                    <Flex center key={s.id} gap='1rem'>
                        <Input
                            name='name'
                            onChange={(e) => handleChange(e, s.id)}
                            placeholder='Fuel Type...'
                            labelText='Name'
                            nomarg
                            value={s.name}
                        />
                        <HonestSelectInput
                            nomarg
                            name='type'
                            onChange={(v: ISelectValue, n) => handleSelect(v, n, s.id)}
                            options={fieldTypes.slice(0, -1)}
                            labelText='Type'
                            defaultValue={s.type?.value}
                        />
                        {state.dropDownItems.length > 2 && (
                            <CursorPointer
                                style={{ marginTop: 20 }}
                                onClick={() => handleRemoveField(s.id)}
                            >
                                <RemoveCircle />
                            </CursorPointer>
                        )}
                    </Flex>
                ))}
                {state.dataType?.value == 3 && (
                    <Flex width='100%' mb='16'>
                        <CursorPointer onClick={handleAddSubfield}>
                            <AddCircle />
                        </CursorPointer>
                    </Flex>
                )}
                <Flex width='100%' gap='1rem'>
                    {state.dataType?.value == 2 && (
                        <HonestSelectAsyncInput
                            onChange={handleUnit}
                            labelText='Measurement unit'
                            width='50%'
                            nomarg
                            name='unit'
                            link='units'
                            defaultValue={state?.unit?.label}
                            parserFunction={(data: any) => data.map((i: any) => ({ label: i.name, value: i.id }))}
                        />
                    )}

                </Flex>
            </Flex>
        </ModalScrollContainer>
        <Flex width='100%' justcenter >
            <Button disabled={isDisabled} orange onClick={handleSave} >Save</Button>
        </Flex>
    </Modal >
}

export interface IInventoryFieldsModal {
    id?: number
    label: string,
    dataType: ISelectValue,
    unit: ISelectValue,
    helpText: string,
    dropDownItems: {
        id: number
        name: string,
        type: ISelectValue
    }[]
}


export const fieldTypes = [
    { label: 'Text', value: 1 },
    { label: 'Number', value: 2 },
    { label: 'Dropdown', value: 3 },
]
